import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Printer, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtMoney = (n) => (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const iso = (d) => d.toISOString().slice(0, 10);

const Line = ({ label, value, bold, indent, accent, testId, note }) => (
  <div className={`flex items-baseline justify-between gap-3 py-1 ${indent ? "pl-6" : ""} ${bold ? "border-t border-border/50 mt-1 pt-2" : ""}`} data-testid={testId}>
    <span className={`font-mono text-xs ${bold ? "font-bold text-card-foreground" : "text-muted-foreground"}`}>
      {label}
      {note && <span className="ml-2 text-[9px] uppercase text-amber-400">{note}</span>}
    </span>
    <span className={`font-mono tabular-nums ${bold ? "font-bold text-base" : "text-xs"} ${accent || "text-card-foreground"}`}>
      {typeof value === "number" ? `$${fmtMoney(value)}` : value}
    </span>
  </div>
);

export default function ProfitLossTab() {
  const today = new Date();
  const [startDate, setStartDate] = useState(iso(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [endDate, setEndDate] = useState(iso(today));
  const [pl, setPl] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/profit-loss`, { params: { start_date: startDate, end_date: endDate } });
      setPl(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load P&L");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const preset = (p) => {
    const now = new Date();
    if (p === "today") { setStartDate(iso(now)); setEndDate(iso(now)); }
    else if (p === "week") { const s = new Date(now); s.setDate(now.getDate() - 6); setStartDate(iso(s)); setEndDate(iso(now)); }
    else if (p === "month") { setStartDate(iso(new Date(now.getFullYear(), now.getMonth(), 1))); setEndDate(iso(now)); }
    else if (p === "year") { setStartDate(iso(new Date(now.getFullYear(), 0, 1))); setEndDate(iso(now)); }
  };

  const exportFile = (format) => {
    window.open(`${API}/reports/profit-loss/export?start_date=${startDate}&end_date=${endDate}&format=${format}`, "_blank");
  };

  const s = pl?.sales, c = pl?.cogs;
  const net = pl?.net_profit || 0;
  const selCls = "h-9 px-2 rounded-md border border-border bg-background font-mono text-xs";

  return (
    <div className="space-y-4" data-testid="pnl-tab">
      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={selCls} data-testid="pnl-start" />
        </div>
        <div>
          <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={selCls} data-testid="pnl-end" />
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => preset("today")} data-testid="pnl-preset-today">Today</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => preset("week")} data-testid="pnl-preset-week">Week</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => preset("month")} data-testid="pnl-preset-month">Month</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => preset("year")} data-testid="pnl-preset-year">Year</Button>
        </div>
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={load} data-testid="pnl-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => exportFile("csv")} data-testid="pnl-export-csv"><Download className="w-3.5 h-3.5 mr-1" />CSV</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => exportFile("xlsx")} data-testid="pnl-export-xlsx"><Download className="w-3.5 h-3.5 mr-1" />Excel</Button>
          <Button size="sm" variant="outline" className="btn-3d h-9" onClick={() => window.print()} data-testid="pnl-print"><Printer className="w-3.5 h-3.5 mr-1" />Print / PDF</Button>
        </div>
      </div>

      {pl && (
        <Card className="border-border/50 max-w-2xl" data-testid="pnl-statement">
          <CardHeader className="pb-1">
            <CardTitle className="font-mono text-sm flex items-center justify-between">
              <span>Profit &amp; Loss</span>
              <span className="text-xs text-muted-foreground font-normal">{pl.start_date} → {pl.end_date} · {pl.docs_scanned} day{pl.docs_scanned === 1 ? "" : "s"} of paperwork</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <p className="font-mono text-[10px] uppercase tracking-wider text-sky-300 mb-1">Sales (from Z-Report — automatic)</p>
            <Line label="Inside Sales" value={s.inside_sales} indent testId="pnl-inside-sales" />
            <Line label="Lottery Sales (Instant + Online)" value={s.lottery_sales} indent testId="pnl-lottery-sales" />
            <Line label="Fuel Sales" value={s.fuel_sales} indent testId="pnl-fuel-sales" />
            <Line label="Total Sales" value={s.total_sales} bold testId="pnl-total-sales" />
            <Line label="Sales Returns" value="not tracked" indent testId="pnl-sales-returns" />
            <Line label="Net Sales" value={s.net_sales} bold testId="pnl-net-sales" />

            <p className="font-mono text-[10px] uppercase tracking-wider text-sky-300 mt-4 mb-1">Cost of Goods — “Sales minus Purchases” method</p>
            <Line label="Opening Inventory" value="not tracked" indent testId="pnl-opening-inv" />
            <Line label="Total Purchases (Supplier Payments — automatic)" value={c.total_purchases} indent testId="pnl-purchases" />
            <Line label="Purchase Returns" value="not tracked" indent testId="pnl-purchase-returns" />
            <Line label="Closing Inventory" value="not tracked" indent testId="pnl-closing-inv" />
            <Line label="Cost of Goods Sold" value={c.cost_of_goods_sold} bold note="= purchases (not true COGS)" testId="pnl-cogs" />
            <p className="font-mono text-[10px] text-amber-400/90 mt-1" data-testid="pnl-cogs-note">{c.note}</p>

            <Line label="Gross Profit (Sales minus Purchases)" value={pl.gross_profit} bold accent={pl.gross_profit >= 0 ? "text-emerald-400" : "text-rose-400"} testId="pnl-gross-profit" />

            <p className="font-mono text-[10px] uppercase tracking-wider text-sky-300 mt-4 mb-1">Other Income (manual entries)</p>
            {pl.other_income_by_category.map((i) => (
              <Line key={i.category} label={i.category} value={i.amount} indent testId={`pnl-oi-${i.category.replace(/\s/g, "_")}`} />
            ))}
            <Line label="Other Income Total" value={pl.other_income} bold testId="pnl-other-income" />
            <Line label="Total Income (Gross Profit + Other Income)" value={pl.total_income} bold testId="pnl-total-income" />

            <p className="font-mono text-[10px] uppercase tracking-wider text-sky-300 mt-4 mb-1">Operating &amp; Other Expenses (manual entries)</p>
            {pl.other_expenses_by_category.map((i) => (
              <Line key={i.category} label={i.category} value={i.amount} indent testId={`pnl-oe-${i.category.replace(/\s/g, "_")}`} />
            ))}
            <Line label="Total Expenses" value={pl.other_expenses} bold testId="pnl-other-expenses" />

            <div className={`mt-4 rounded-lg px-4 py-3 border-2 flex items-center justify-between ${net >= 0 ? "border-emerald-500/60 bg-emerald-500/10" : "border-rose-500/60 bg-rose-500/10"}`} data-testid="pnl-net-box">
              <span className="font-mono text-sm font-bold">{net >= 0 ? "NET PROFIT" : "NET LOSS"}</span>
              <span className={`font-mono font-bold text-xl tabular-nums ${net >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="pnl-net-value">
                ${fmtMoney(Math.abs(net))}
              </span>
            </div>
            <p className="font-mono text-[10px] text-muted-foreground mt-2">
              Taxes collected in period (State + City Beer, informational): ${fmtMoney(pl.taxes_collected)}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
